import { InvalidAddressError } from './errors'
import { IPV4_MAX, IPV4_SPACE, type Cidr } from './types'

const OCTETS = 4

/** Parses dotted-quad notation into an unsigned 32-bit number. */
export function parseIpv4(input: string): number {
  const text = input.trim()
  if (text === '') throw new InvalidAddressError(input, 'empty')

  const parts = text.split('.')
  if (parts.length !== OCTETS) {
    throw new InvalidAddressError(input, `expected ${OCTETS} octets, got ${parts.length}`)
  }

  let value = 0
  for (const part of parts) {
    // Reject '', '1e2', '0x10', '+1', ' 1', and leading zeros like '010' which some
    // resolvers read as octal — ambiguity is a bug source in network tooling.
    if (!/^(0|[1-9][0-9]{0,2})$/.test(part)) {
      throw new InvalidAddressError(input, `malformed octet "${part}"`)
    }
    const octet = Number(part)
    if (octet > 255) throw new InvalidAddressError(input, `octet ${octet} exceeds 255`)
    value = value * 256 + octet
  }
  return value
}

/** Formats an unsigned 32-bit number as dotted-quad notation. */
export function formatIpv4(address: number): string {
  assertAddress(address)
  return [
    (address >>> 24) & 255,
    (address >>> 16) & 255,
    (address >>> 8) & 255,
    address & 255,
  ].join('.')
}

/** Parses `a.b.c.d/n`. A bare address without a prefix is treated as `/32`. */
export function parseCidr(input: string): Cidr {
  const text = input.trim()
  const slash = text.indexOf('/')
  if (slash === -1) return { address: parseIpv4(text), prefix: 32 }

  const address = parseIpv4(text.slice(0, slash))
  const prefixText = text.slice(slash + 1).trim()
  if (!/^(0|[1-9][0-9]?)$/.test(prefixText)) {
    throw new InvalidAddressError(input, `malformed prefix "${prefixText}"`)
  }
  const prefix = Number(prefixText)
  if (prefix > 32) throw new InvalidAddressError(input, `prefix /${prefix} exceeds /32`)
  return { address, prefix }
}

/** Formats a CIDR block, normalised to its network address. */
export function formatCidr(cidr: Cidr): string {
  return `${formatIpv4(networkAddress(cidr))}/${cidr.prefix}`
}

/** Subnet mask for a prefix length, as an unsigned 32-bit number. */
export function maskFromPrefix(prefix: number): number {
  assertPrefix(prefix)
  // `-1 << 32` is a no-op shift in JS (the count is taken mod 32), so /0 is special.
  return prefix === 0 ? 0 : (-1 << (32 - prefix)) >>> 0
}

/** Inverse (wildcard / host) mask for a prefix length. */
export function wildcardMask(prefix: number): number {
  return ~maskFromPrefix(prefix) >>> 0
}

/** Prefix length for a contiguous subnet mask. Throws if the mask has gaps. */
export function prefixFromMask(mask: number): number {
  assertAddress(mask)
  const inverted = ~mask >>> 0
  // A valid mask is a run of 1s then 0s, so its inverse must be 2^k - 1.
  if (((inverted + 1) & inverted) !== 0) {
    throw new InvalidAddressError(formatIpv4(mask), 'mask is not contiguous')
  }
  let prefix = 32
  let remaining = inverted
  while (remaining > 0) {
    prefix -= 1
    remaining = Math.floor(remaining / 2)
  }
  return prefix
}

/** First address of the block (the network address). */
export function networkAddress(cidr: Cidr): number {
  assertCidr(cidr)
  return (cidr.address & maskFromPrefix(cidr.prefix)) >>> 0
}

/** Last address of the block. For prefixes <= 30 this is the broadcast address. */
export function lastAddress(cidr: Cidr): number {
  return networkAddress(cidr) + addressCount(cidr.prefix) - 1
}

/** Total addresses in the block, reserved ones included. */
export function addressCount(prefix: number): number {
  assertPrefix(prefix)
  return IPV4_SPACE / 2 ** prefix
}

/** True when `inner` lies entirely within `outer`. */
export function contains(outer: Cidr, inner: Cidr): boolean {
  if (inner.prefix < outer.prefix) return false
  return networkAddress({ address: inner.address, prefix: outer.prefix }) === networkAddress(outer)
}

/** Splits a block into its two halves. Throws at `/32`, which cannot be split. */
export function split(cidr: Cidr): [Cidr, Cidr] {
  assertCidr(cidr)
  if (cidr.prefix === 32) {
    throw new InvalidAddressError(formatCidr(cidr), 'a /32 cannot be split further')
  }
  const prefix = cidr.prefix + 1
  const network = networkAddress(cidr)
  return [
    { address: network, prefix },
    { address: network + addressCount(prefix), prefix },
  ]
}

/** The block one prefix shorter that contains this one. Throws at `/0`. */
export function parent(cidr: Cidr): Cidr {
  assertCidr(cidr)
  if (cidr.prefix === 0) {
    throw new InvalidAddressError(formatCidr(cidr), 'a /0 has no parent')
  }
  const prefix = cidr.prefix - 1
  return { address: networkAddress({ address: cidr.address, prefix }), prefix }
}

/** The other half of this block's parent. Throws at `/0`. */
export function sibling(cidr: Cidr): Cidr {
  const [first, second] = split(parent(cidr))
  return networkAddress(cidr) === networkAddress(first) ? second : first
}

/** True when both blocks are the two halves of a common parent. */
export function areSiblings(a: Cidr, b: Cidr): boolean {
  if (a.prefix !== b.prefix || a.prefix === 0) return false
  return networkAddress(sibling(a)) === networkAddress(b)
}

/** Iterates the subnets of `cidr` at `prefix`. Lazy — a /8 split to /32 is 16M blocks. */
export function* subnetsOf(cidr: Cidr, prefix: number): Generator<Cidr> {
  assertPrefix(prefix)
  if (prefix < cidr.prefix) {
    throw new InvalidAddressError(formatCidr(cidr), `/${prefix} is larger than the block`)
  }
  const step = addressCount(prefix)
  const start = networkAddress(cidr)
  const end = start + addressCount(cidr.prefix)
  for (let address = start; address < end; address += step) {
    yield { address, prefix }
  }
}

function assertAddress(address: number): void {
  if (!Number.isInteger(address) || address < 0 || address > IPV4_MAX) {
    throw new InvalidAddressError(String(address), 'not a 32-bit unsigned integer')
  }
}

function assertPrefix(prefix: number): void {
  if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) {
    throw new InvalidAddressError(String(prefix), 'prefix must be an integer 0..32')
  }
}

function assertCidr(cidr: Cidr): void {
  assertAddress(cidr.address)
  assertPrefix(cidr.prefix)
}
